import React, {useState, useEffect, useCallback} from 'react';
import { Link } from 'react-router-dom';
import { clientsApi } from '../api/api';
import { getDropdownOptions } from '../utils/dropdownConfig';
import WorkflowProgress from './WorkflowProgress';
import './ClientList.css';

function ClientList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    partnerName: '',
    status: 'Folyamatban',
    location: '',
    contractMaxPower: '',
    type: 'PV',
    integrator: '',
    interface: '',
    podId: '',
    partnerContact: '',
    alteoManager: '',
    joinDate: '',
    leaveDate: '',
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Sorting state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Dropdown options state
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [integratorOptions, setIntegratorOptions] = useState([]);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getAll({
        page,
        limit,
        sortBy,
        sortOrder,
        search: searchTerm
      });
      setClients(response.data.data);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  }, [limit, page, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [status, type, integrator] = await Promise.all([
          getDropdownOptions('status'),
          getDropdownOptions('type'),
          getDropdownOptions('integrator'),
        ]);
        setStatusOptions(status);
        setTypeOptions(type);
        setIntegratorOptions(integrator);

        // Set default integrator if available
        if (integrator.length > 0 && !formData.integrator) {
          setFormData(prev => ({ ...prev, integrator: integrator[0] }));
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, [formData.integrator]);

  useEffect(() => {
    fetchClients();
  }, [page, limit, sortBy, sortOrder, searchTerm, fetchClients]);

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setPage(1); // Reset to first page
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleExportToExcel = async () => {
    try {
      const response = await clientsApi.exportToExcel();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (e, fieldName) => {
    const value = e.target.value;
    // Allow only numbers, dots, and spaces
    const cleaned = value.replace(/[^\d.\s]/g, '');
    setFormData({
      ...formData,
      [fieldName]: cleaned,
    });
  };

 /* const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}. ${month}. ${day}.`;
  };*/

  const parseDateFromHungarian = (dateString) => {
    if (!dateString) return null;
    // Try to parse yyyy. MM. dd. format
    const match = dateString.match(/(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toISOString();
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert Hungarian date format to ISO before sending
      const submitData = {
        ...formData,
        joinDate: parseDateFromHungarian(formData.joinDate) || formData.joinDate,
        leaveDate: parseDateFromHungarian(formData.leaveDate) || formData.leaveDate,
      };
      await clientsApi.create(submitData);
      setShowModal(false);
      setFormData({
        partnerName: '',
        status: statusOptions[0] || 'Folyamatban',
        location: '',
        contractMaxPower: '',
        type: typeOptions[0] || 'PV',
        integrator: integratorOptions[0] || '',
        interface: '',
        podId: '',
        partnerContact: '',
        alteoManager: '',
        joinDate: '',
        leaveDate: '',
      });
      fetchClients();
    } catch (error) {
      console.error('Error creating client:', error);
      alert('Error creating client. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await clientsApi.delete(id);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  const getStatusClass = (status) => {
    if (status === 'Aktív') return 'status-active';
    if (status === 'Nem aktív') return 'status-inactive';
    return 'status-progress';
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return ' ↕';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  if (loading) {
    return <div className="card">Loading...</div>;
  }

  return (
    <div className="client-list">
      <div className="card">
        <div className="header-row">
          <h2>Clients ({total})</h2>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button className="btn btn-success" onClick={handleExportToExcel}>
              Export to Excel
            </button>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              Add New Client
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search clients..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                flex: 1,
                padding: '0.5rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
            {searchTerm && (
              <button type="button" className="btn btn-secondary" onClick={handleClearSearch}>
                Clear
              </button>
            )}
          </form>
          {searchTerm && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
              Searching for: "{searchTerm}"
            </p>
          )}
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('sequenceNumber')} style={{ cursor: 'pointer' }}>
                  #ID{getSortIcon('sequenceNumber')}
                </th>
                <th onClick={() => handleSort('partnerName')} style={{ cursor: 'pointer' }}>
                  Partner Name{getSortIcon('partnerName')}
                </th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                  Status{getSortIcon('status')}
                </th>
                <th onClick={() => handleSort('location')} style={{ cursor: 'pointer' }}>
                  Location{getSortIcon('location')}
                </th>
                <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                  Type{getSortIcon('type')}
                </th>
                <th onClick={() => handleSort('podId')} style={{ cursor: 'pointer' }}>
                  POD ID{getSortIcon('podId')}
                </th>
                <th onClick={() => handleSort('integrator')} style={{ cursor: 'pointer' }}>
                  Integrator{getSortIcon('integrator')}
                </th>
                <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                  Created{getSortIcon('createdAt')}
                </th>
                <th>Integration Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client._id}>
                  <td>{client.sequenceNumber}</td>
                  <td>{client.partnerName}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(client.status)}`}>
                      {client.status}
                    </span>
                  </td>
                  <td>{client.location}</td>
                  <td>{client.type}</td>
                  <td>{client.podId}</td>
                  <td>{client.integrator}</td>
                  <td>{client.createdAt ? new Date(client.createdAt).toLocaleDateString('hu-HU') : '-'}</td>
                  <td>
                    <WorkflowProgress workflowSteps={client.workflowSteps} />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link to={`/client/${client._id}`} className="btn btn-primary btn-sm">
                        View
                      </Link>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(client._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="pagination-container">
          <div className="pagination-info">
            <label>
              Rows per page:
              <select value={limit} onChange={handleLimitChange}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
                <option value="500">500</option>
              </select>
            </label>
            <span>
              Page {page} of {totalPages} (Total: {total} clients)
            </span>
          </div>
          <div className="pagination-buttons">
            <button
              className="btn btn-sm"
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
            >
              First
            </button>
            <button
              className="btn btn-sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              Previous
            </button>
            <button
              className="btn btn-sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
            <button
              className="btn btn-sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={page === totalPages}
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Client</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Partner Name *</label>
                <input
                  type="text"
                  name="partnerName"
                  value={formData.partnerName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  {statusOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Contract Max Power (kW)</label>
                <input
                  type="text"
                  name="contractMaxPower"
                  value={formData.contractMaxPower}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select name="type" value={formData.type} onChange={handleInputChange}>
                  {typeOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Integrator</label>
                <select name="integrator" value={formData.integrator} onChange={handleInputChange}>
                  {integratorOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Interface</label>
                <input
                  type="text"
                  name="interface"
                  value={formData.interface}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>POD ID</label>
                <input
                  type="text"
                  name="podId"
                  value={formData.podId}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Partner Contact</label>
                <input
                  type="text"
                  name="partnerContact"
                  value={formData.partnerContact}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Alteo Manager</label>
                <input
                  type="text"
                  name="alteoManager"
                  value={formData.alteoManager}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Join Date</label>
                <input
                  type="text"
                  name="joinDate"
                  value={formData.joinDate}
                  onChange={(e) => handleDateChange(e, 'joinDate')}
                  placeholder="yyyy. MM. dd."
                />
              </div>

              <div className="form-group">
                <label>Leave Date</label>
                <input
                  type="text"
                  name="leaveDate"
                  value={formData.leaveDate}
                  onChange={(e) => handleDateChange(e, 'leaveDate')}
                  placeholder="yyyy. MM. dd."
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-success">
                  Create Client
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientList;
